import type { ActivityInput, Area, Measure, TargetInput, TreeInput } from './api';

export type Template = TreeInput & { id: string; area: Area; title: string; description: string };

export const SHEET_HEADINGS = [
  'What counts as right (with examples):',
  'How to present it:',
  'Help to give and how to reduce it:',
  'After a wrong answer:',
  'Reward:',
  'When it counts as learned:',
];

export const BASELINE = 'Describe what happens now';

type Sheet = [string, string, string, string, string, string];

const sheet = (parts: Sheet) => SHEET_HEADINGS.map((h, i) => `${h}\n${parts[i]}`).join('\n\n');

const target = (statement: string, successCriterion: string, provisionWhat: string, provisionWho: string, provisionHowOften: string, provisionMinutes: number): TargetInput => ({
  statement, baseline: BASELINE, successCriterion, byDate: null, reviewDate: null,
  provisionWhat, provisionWho, provisionHowOften, provisionMinutes, status: 'WORKING',
});

const activity = (title: string, measure: Measure, parts: Sheet): ActivityInput => ({ kind: 'AWAY', programId: null, title, instructions: sheet(parts), measure });

const goal = (area: Area, statement: string) => ({ statement, area, source: 'FAMILY' as const, byDate: null, baseline: null, gas: null, status: 'WORKING' as const });

export const TEMPLATES: Template[] = [
  {
    id: 'asking-for-help',
    area: 'COMMUNICATION',
    title: 'Asking for help',
    description: 'Lets your learner tell someone when they are stuck, in words, signs, symbols or a help card.',
    goal: goal('COMMUNICATION', 'When something is hard or out of reach, my learner lets an adult know they would like help, in the way that works best for them.'),
    targets: [
      {
        target: target(
          'Asks for help with a word, sign, symbol or help card when stuck, in 8 of 10 set-up chances, on 3 different days.',
          '8 of 10 chances with no help from the adult, on 3 different days.',
          'Short practice where small problems are set up on purpose, such as a tight lid or a toy out of reach.',
          'Parent or teaching assistant', '4 times a week', 10,
        ),
        activities: [
          activity('Set-up chances to ask for help', 'TRIALS', [
            'Your learner asks for help in any clear way: says help or help please, signs help, hands you a help card, or taps help on their device. Pulling your hand towards the item counts as a first step, not yet as asking.',
            'Choose something your learner really wants. Put it in a jar they cannot open, or on a shelf they cannot reach. Stay close by and wait quietly for about 5 seconds. Do not ask what they need.',
            'Start by holding up the help card or modelling the sign as soon as they look stuck. Next, point to the card without speaking. Then just wait with an expectant look. Move to the lighter help after two good sessions.',
            'Calmly show the help card or sign again, let them copy it, then help straight away. Never hold back help to force an answer.',
            'Helping at once is the reward: open the jar or pass the toy, and say what they did, for example: You asked for help.',
            'Asks without any help in 8 of 10 chances on 3 different days, and has also asked at least once with a different adult.',
          ]),
        ],
      },
    ],
  },
  {
    id: 'requesting-with-aac',
    area: 'COMMUNICATION',
    title: 'Requesting with AAC',
    description: 'Builds asking for favourite things using a communication book, picture cards or a speech device.',
    goal: goal('COMMUNICATION', 'My learner uses their communication system to ask for things they want, so they are understood without needing to rely on behaviour.'),
    targets: [
      {
        target: target(
          'Uses their AAC to ask for a wanted item or activity in 8 of 10 chances, with 3 different items.',
          '8 of 10 requests made without physical help, across 3 items.',
          'Requesting practice built into snack time and play, with the AAC always within reach.',
          'Parent and teaching assistant', 'Every day', 15,
        ),
        activities: [
          activity('Requesting at snack and play', 'TRIALS', [
            'Your learner selects the symbol or button for the item they want, or hands over its picture card. For example, pressing biscuit when biscuits are offered. Any request on the device counts, even if they also speak.',
            'Hold up two or three favourite things where they can see them but not reach. Keep the device or book next to your learner. Wait about 5 seconds. Model by pressing the button yourself when you talk about the item.',
            'Start by gently guiding their hand to the symbol. Next, point to the symbol. Then simply wait. Always model on the device as you speak, at every level.',
            'Model the right symbol on the device, say the word, and give the item anyway. We never withhold things to make someone communicate.',
            'The item they asked for, straight away. Respond warmly to every attempt.',
            'Asks without physical help in 8 of 10 chances, with 3 different items, on 3 different days.',
          ]),
          activity('Requests during the day', 'COUNT', [
            'Any time your learner uses their AAC to ask for something without being prompted. For example, pressing drink at lunch, or outside at the door.',
            'Keep the AAC with your learner all day and in every room. Make wanted things a little hard to get, so there are natural reasons to ask.',
            'Model on the device during ordinary talk, without expecting a reply. Reduce modelling only once spontaneous requests are clearly growing.',
            'There are no wrong answers here. If a request is unclear, model the word you think they meant and respond to it.',
            'Responding to the request as quickly as you can.',
            'At least 10 spontaneous requests a day, on 5 days in a row.',
          ]),
        ],
      },
    ],
  },
  {
    id: 'naming-objects',
    area: 'COMMUNICATION',
    title: 'Naming common objects',
    description: 'Builds naming everyday things in speech, sign or AAC, starting with things your learner cares about.',
    goal: goal('COMMUNICATION', 'My learner can name everyday objects, so they can tell others about the things around them.'),
    targets: [
      {
        target: target(
          'Names 20 common objects from home and school, in speech, sign or AAC, without help.',
          'Names each of the 20 objects correctly on 2 different days.',
          'Short naming games with real objects and photos.',
          'Parent', '5 times a week', 10,
        ),
        activities: [
          activity('Naming real objects', 'TRIALS', [
            'Your learner names the object in their own way: a clear word, a close attempt, a sign or the right AAC symbol. For example, nana for banana counts.',
            'Use real objects first, then photos. Work on 5 objects at a time, mixed with ones they already know. Hold one up and ask: What is it? Wait about 5 seconds.',
            'Start by saying the name straight after your question for them to copy. Next, say only the first sound. Then just wait. Reduce help one object at a time.',
            'Say the name calmly, let them copy it, then move on to an easy one they know. No telling off and no repeated drilling.',
            'Praise and a moment with the object if they like it, or a turn of a favourite game after a few tries.',
            'Names the object without help on 2 different days, then it joins the known set and a new object comes in.',
          ]),
        ],
      },
    ],
  },
  {
    id: 'two-step-instructions',
    area: 'COMMUNICATION',
    title: 'Following 2-step instructions',
    description: 'Builds understanding of instructions with two parts, such as get your coat and put on your shoes.',
    goal: goal('COMMUNICATION', 'My learner understands and follows everyday instructions with two parts, so daily routines go more smoothly.'),
    targets: [
      {
        target: target(
          'Follows a 2-step instruction given once, in 8 of 10 tries, on 3 different days.',
          '8 of 10 instructions completed in the right order with no repeats or gestures.',
          'Short instruction games, and real instructions during routines.',
          'Parent or teaching assistant', '4 times a week', 10,
        ),
        activities: [
          activity('Two-step instruction game', 'TRIALS', [
            'Your learner does both parts, in order, after hearing the instruction once. For example: Get the ball and put it in the box. Doing only one part is not yet right.',
            'Get their attention first by saying their name. Give the instruction once, slowly and clearly, with the key words stressed. Use objects in front of them to begin with.',
            'Start by pointing to each object as you say it. Next, show the first part only. Then give the words alone. A visual card with two pictures can help early on and be removed later.',
            'Calmly repeat the instruction with a point to each object, help them finish, and try an easier one next.',
            'Specific praise, such as You got the ball and put it in the box. Make the game itself fun.',
            '8 of 10 right with words alone on 3 different days, and also following 2-step instructions during real routines.',
          ]),
        ],
      },
    ],
  },
  {
    id: 'visual-schedule',
    area: 'COGNITION',
    title: 'Following a visual schedule',
    description: 'Helps your learner check a picture schedule to know what is happening now and what comes next.',
    goal: goal('COGNITION', 'My learner uses a visual schedule to know what is happening and what comes next, so the day feels more predictable.'),
    targets: [
      {
        target: target(
          'Checks their schedule and goes to the next activity when given a check your schedule cue, in 8 of 10 changes, on 3 different days.',
          '8 of 10 schedule checks done with only the spoken cue.',
          'A picture or object schedule used for every change of activity at home or school.',
          'Parent and class staff', 'Every day', 5,
        ),
        activities: [
          activity('Checking the schedule', 'TRIALS', [
            'Your learner goes to the schedule, takes or looks at the next picture, and goes to that activity. For example, taking the snack picture and going to the table.',
            'Keep the schedule in the same place at their eye level. Use 3 to 5 pictures, top to bottom or left to right. At each change, give the cue: Check your schedule.',
            'Start by walking with them and guiding their hand to the picture. Next, point to the schedule. Then give the spoken cue only. Step back a little further each time.',
            'Walk them gently to the schedule and show the next picture. Keep it calm and unhurried. If the change is upsetting, give time and do not insist.',
            'The next activity itself, especially if it is a liked one. Put favourite things on the schedule often.',
            'Uses the schedule with only the spoken cue in 8 of 10 changes on 3 different days.',
          ]),
        ],
      },
    ],
  },
  {
    id: 'matching-sorting',
    area: 'COGNITION',
    title: 'Matching and sorting',
    description: 'Builds matching pictures and objects, then sorting them into groups by colour, shape or type.',
    goal: goal('COGNITION', 'My learner can match and sort things into groups, a step towards early number and organising their own belongings.'),
    targets: [
      {
        target: target(
          'Matches identical pictures from a choice of 3, in 8 of 10 tries.',
          '8 of 10 right with no help, on 3 different days.',
          'Table-top matching tasks with pictures and real objects.',
          'Parent or teaching assistant', '4 times a week', 10,
        ),
        activities: [
          activity('Picture matching', 'TRIALS', [
            'Your learner puts the picture on the same picture from a choice of 3. For example, putting the cat picture on the cat.',
            'Lay out 3 pictures in a row. Hand one picture over and say: Match. Change the position of the right answer each time.',
            'Start by guiding their hand to the right place. Next, point to it. Then use a choice of 2 before moving back to 3. Finally, give the word alone.',
            'Show the right place, let them put it there, and give the next one. Keep the pace easy.',
            'Praise, and something they enjoy after every few tries, such as a short song or a favourite toy.',
            '8 of 10 right without help on 3 different days, then move on to sorting into 2 groups.',
          ]),
        ],
      },
      {
        target: target(
          'Sorts objects into 2 groups by colour or type, with 10 objects, without help.',
          'All 10 objects sorted right on 2 different days.',
          'Sorting tasks using everyday things, such as socks, cutlery or toy animals.',
          'Parent', '3 times a week', 10,
        ),
        activities: [
          activity('Sorting into groups', 'TRIALS', [
            'Each object goes into the right group. For example, spoons in one pot and forks in the other.',
            'Put one example in each pot to start. Hand objects over one at a time and say: Sort.',
            'Start by pointing to the right pot. Then just hand over the object and wait. Increase the number of objects slowly.',
            'Show the right pot and let them move it there. No fuss.',
            'Praise, and helping to tidy away together if they enjoy it.',
            'All 10 objects sorted without help on 2 different days.',
          ]),
        ],
      },
    ],
  },
  {
    id: 'waiting',
    area: 'SEMH',
    title: 'Waiting',
    description: 'Builds waiting calmly for a short time, with a clear signal for when the wait is over.',
    goal: goal('SEMH', 'My learner can wait for a short time for something they want, with support that makes waiting easier.'),
    targets: [
      {
        target: target(
          'Waits calmly for up to 2 minutes using a wait card or timer, on 4 of 5 chances.',
          'Waits 2 minutes calmly in 4 of 5 chances, on 3 different days.',
          'Planned short waits during the day, starting very short and growing slowly.',
          'Parent and teaching assistant', 'Every day', 5,
        ),
        activities: [
          activity('Waiting with a wait card', 'DURATION', [
            'Your learner stays near the wanted thing and holds the wait card or watches the timer until it ends, without grabbing. Fidgeting, moving and stimming are all fine. Record how long they waited.',
            'Show the wanted thing, hand over the wait card and say: Wait. Start the visual timer. Begin with a wait so short they will succeed, such as 5 seconds.',
            'Stay close and calm. Offer something to hold or do while waiting. Increase the time only after 3 easy waits in a row, by a few seconds at a time.',
            'If waiting is too hard, end the wait early, give the thing, and make the next wait shorter. Waiting should never become a struggle.',
            'When the timer ends, they get the thing straight away, with praise: You waited.',
            'Waits 2 minutes calmly in 4 of 5 chances on 3 different days, and in at least one real situation such as a queue.',
          ]),
        ],
      },
    ],
  },
  {
    id: 'turn-taking',
    area: 'SEMH',
    title: 'Turn-taking',
    description: 'Builds taking turns in a simple game with one other person, using a clear my turn and your turn cue.',
    goal: goal('SEMH', 'My learner can take turns in a simple game with another person, so they can join in play with others.'),
    targets: [
      {
        target: target(
          'Takes 5 turns back and forth in a simple game with an adult, waiting for their turn.',
          '5 turns in a row on 3 different days.',
          'Short turn-taking games such as rolling a ball, building a tower or a simple board game.',
          'Parent or sibling with an adult nearby', '4 times a week', 10,
        ),
        activities: [
          activity('Turn-taking game', 'COUNT', [
            'Your learner takes their turn and then lets the other person have theirs, without taking the item back early. Record how many turns in a row they took. For example, rolling the ball and waiting for it to come back.',
            'Choose a game with clear, quick turns. Use a turn card or say: My turn, then: Your turn. Keep each turn short so waiting is easy.',
            'Start with very quick turns and gently hold their hand during your turn if they want that. Next, use the turn card alone. Then just the words.',
            'If they take the item during your turn, calmly say: My turn, finish quickly, and pass it back. Keep the game fun.',
            'Their next turn, plus praise for waiting: Great waiting.',
            '5 turns in a row on 3 different days, then try with another child.',
          ]),
        ],
      },
    ],
  },
  {
    id: 'transitions',
    area: 'SEMH',
    title: 'Moving between activities',
    description: 'Supports moving from one activity to the next with warnings, a timer and something to carry.',
    goal: goal('SEMH', 'My learner can move from one activity to the next feeling calm and prepared.'),
    targets: [
      {
        target: target(
          'Moves from a liked activity to the next one with a warning and a timer, calmly enough to rate 4 or 5, in 4 of 5 changes.',
          'Rated 4 or 5 in 4 of 5 changes, over 2 weeks.',
          'Planned warnings before every change, a visual timer and a transition object.',
          'Parent and class staff', 'Every day', 5,
        ),
        activities: [
          activity('Supported change of activity', 'RATING', [
            'Rate how the change went: 1 means very hard and upset, 3 means managed with lots of support, 5 means calm and moved on their own. Any calm change counts, whatever it looks like.',
            'Give a warning 5 minutes before and again at 1 minute, with the visual timer in view. When the timer ends, say: Finished, then show what is next. Offer an object to carry, such as the picture of the next activity.',
            'Start by walking with them and carrying the object together. Next, hand over the object and walk alongside. Then give the warning alone.',
            'If the change is very hard, pause. Give time and space, lower demands, and try again when calm. Do not use threats or count down.',
            'Make the next activity something they enjoy at first, and praise calm moving on.',
            'Rated 4 or 5 in 4 of 5 changes over 2 weeks.',
          ]),
        ],
      },
    ],
  },
  {
    id: 'toileting',
    area: 'SENSORY',
    title: 'Toileting routine',
    description: 'Teaches the steps of using the toilet with a picture sequence, at a pace your learner is comfortable with.',
    goal: goal('SENSORY', 'My learner follows their toilet routine with growing independence, feeling safe and comfortable.'),
    targets: [
      {
        target: target(
          'Completes the toilet routine steps with a picture sequence and no more than spoken reminders, on 4 of 5 visits.',
          'All steps done with spoken reminders at most, on 4 of 5 visits, over a week.',
          'Regular, relaxed toilet visits with a picture sequence on the wall.',
          'Parent and named school staff', 'At set times each day', 5,
        ),
        activities: [
          activity('Toilet routine steps', 'STEPS', [
            'The steps are: go to the toilet, pull down clothes, sit, use the toilet, wipe, pull up clothes, flush, wash hands. Record how many steps they did with at most a spoken reminder.',
            'Visit at regular times, such as after meals. Keep the room calm, warm and quiet. Point to each picture in the sequence as you go.',
            'Start with gentle hand-over-hand help where it is accepted. Next, point to the picture. Then just a spoken reminder. Reduce help on one step at a time, starting with the last step.',
            'There are no wrong answers. If a step is missed, calmly point to the picture. Accidents are dealt with quietly, never with blame.',
            'Something they enjoy straight afterwards, and quiet praise.',
            'All steps with spoken reminders at most, on 4 of 5 visits over a week.',
          ]),
        ],
      },
    ],
  },
  {
    id: 'handwashing',
    area: 'SENSORY',
    title: 'Handwashing',
    description: 'Teaches the steps of washing hands, with attention to how water, soap and towels feel.',
    goal: goal('SENSORY', 'My learner washes their hands on their own after the toilet and before eating.'),
    targets: [
      {
        target: target(
          'Washes hands through all 7 steps with no more than a picture prompt, on 4 of 5 chances.',
          'All 7 steps done with a picture prompt at most, on 4 of 5 chances.',
          'Handwashing practice after the toilet and before snack, with a picture sequence by the sink.',
          'Parent or teaching assistant', 'Several times a day', 3,
        ),
        activities: [
          activity('Handwashing steps', 'STEPS', [
            'The steps are: turn on the tap, wet hands, add soap, rub hands, rinse, turn off the tap, dry hands. Record how many steps they did with at most a picture prompt.',
            'Check the water temperature and soap suit them. Some learners prefer foam soap or a soft towel. Point to each picture as you go.',
            'Start with gentle hand-over-hand help if they are comfortable with it. Next, show the step yourself. Then point to the picture. Reduce help one step at a time.',
            'Calmly point to the picture for the missed step. If the water or soap feels unpleasant, change it rather than insisting.',
            'Praise when done, and what comes next if it is enjoyable, such as snack.',
            'All 7 steps with a picture prompt at most, on 4 of 5 chances.',
          ]),
        ],
      },
    ],
  },
  {
    id: 'dressing',
    area: 'SENSORY',
    title: 'Dressing',
    description: 'Teaches putting on clothes step by step, starting with the last step so your learner finishes each time.',
    goal: goal('SENSORY', 'My learner gets dressed with growing independence in the morning.'),
    targets: [
      {
        target: target(
          'Puts on trousers, top, socks and shoes with no more than spoken reminders, on 4 of 5 mornings.',
          'All steps done with spoken reminders at most, on 4 of 5 mornings.',
          'Dressing practice in the morning routine, with clothes laid out in order.',
          'Parent', 'Every morning', 15,
        ),
        activities: [
          activity('Getting dressed steps', 'STEPS', [
            'The steps are: pants, trousers, top, socks, shoes. Each counts when done with at most a spoken reminder. Clothes on back to front still count.',
            'Lay the clothes out in order, left to right, with a picture card for each. Choose soft clothes without scratchy labels. Allow plenty of time.',
            'Help with all the steps except the last, and let them finish it, such as pulling the top down. When that is easy, let them do the last two steps, and so on.',
            'Calmly point to the next item or picture. If a piece of clothing feels uncomfortable, swap it. Never rush.',
            'Praise for each finished step, and something they enjoy once dressed.',
            'All steps with spoken reminders at most on 4 of 5 mornings.',
          ]),
        ],
      },
    ],
  },
  {
    id: 'new-foods',
    area: 'SENSORY',
    title: 'Trying new foods, gently',
    description: 'Very small, unpressured steps towards being comfortable near a new food, led by your learner.',
    goal: goal('SENSORY', 'My learner feels more comfortable around a few new foods, at their own pace and without pressure.'),
    targets: [
      {
        target: target(
          'Is comfortable with one new food on a separate plate nearby, and touches it when they choose to, on 3 occasions.',
          'Reaches step 3 on the comfort ladder on 3 different days, by their own choice.',
          'Relaxed food play and family meals where the new food is present but never required.',
          'Parent', '3 times a week', 10,
        ),
        activities: [
          activity('Comfort ladder with a new food', 'RATING', [
            'Rate the highest step your learner chose to reach today: 1 food in the room, 2 food on a separate plate near them, 3 touches it, 4 smells it or touches it to their lips, 5 tastes a tiny piece. Any step they choose is a success.',
            'Place a tiny amount of the new food on a separate plate, next to foods they already like. Eat it yourself and talk about it in a relaxed way. Food play, such as stamping shapes or washing vegetables, also counts.',
            'Never prompt, persuade or hold out for a taste. Simply offer the next step, such as: You can touch it if you like. Only move up the ladder when your learner seems relaxed at the current step.',
            'There are no wrong answers. If they say no or move away, accept it straight away and stop. Signs of worry mean stepping back to an easier step next time.',
            'Calm company and their usual foods, always. Do not use food as a reward or remove liked food.',
            'Reaches step 3 by choice on 3 different days. Further steps happen only if your learner is happy to continue.',
          ]),
        ],
      },
    ],
  },
  {
    id: 'noisy-places',
    area: 'SENSORY',
    title: 'Coping with a noisy place',
    description: 'Builds comfort in noisy places step by step, with ear defenders, a break card and a way out.',
    goal: goal('SENSORY', 'My learner has ways to feel comfortable in noisy places, and can ask for a break when they need one.'),
    targets: [
      {
        target: target(
          'Stays in a busy place such as the dinner hall or a shop for 10 minutes using their sensory supports, and asks for a break if needed.',
          '10 minutes on 3 different visits, or a break asked for in their own way.',
          'Short planned visits to noisy places with ear defenders, a break card and a quiet place to go.',
          'Parent and named school staff', '3 times a week', 10,
        ),
        activities: [
          activity('Short visits with sensory supports', 'DURATION', [
            'Record how long your learner stayed comfortably. Using ear defenders, covering ears, moving, stimming or asking for a break all count as coping well. Asking for a break is a success, not a failure.',
            'Show where you are going on a picture first. Start with a short visit at a quiet time. Offer ear defenders before arriving, and agree on the break card and the quiet place to go.',
            'Start by staying very close and offering the ear defenders yourself. Next, let them choose when to use supports. Increase visit length by a minute or two only after easy visits.',
            'If your learner is distressed, leave calmly straight away and go somewhere quiet. Make the next visit shorter. Never insist on staying.',
            'Something they enjoy after the visit, and praise for using their supports: You used your break card.',
            '10 minutes on 3 different visits, with supports they choose.',
          ]),
        ],
      },
    ],
  },
];

export function localiseTemplate(tpl: Template, t: (key: string) => string, statement?: string): TreeInput {
  const tr = (s: string | null) => (s == null ? null : t(s));
  const instructions = (s: string | null) => (s == null ? null : s.split('\n\n').map((block) => {
    const at = block.indexOf('\n');
    return at < 0 ? t(block) : `${t(block.slice(0, at))}\n${t(block.slice(at + 1))}`;
  }).join('\n\n'));
  return {
    goal: { ...tpl.goal, statement: statement ?? t(tpl.goal.statement) },
    targets: tpl.targets.map((x) => ({
      target: {
        ...x.target,
        statement: t(x.target.statement), baseline: tr(x.target.baseline), successCriterion: tr(x.target.successCriterion),
        provisionWhat: tr(x.target.provisionWhat), provisionWho: tr(x.target.provisionWho), provisionHowOften: tr(x.target.provisionHowOften),
      },
      activities: x.activities.map((a) => ({ ...a, title: t(a.title), instructions: instructions(a.instructions) })),
    })),
  };
}
